/**
 * Журнал правок графика и откат (ПЛ-31): кто, что и когда менял; «Вернуть как было до этой правки»
 * откатывает её и все правки после неё одним пакетом — откат тоже попадает в журнал, его можно отменить.
 */
import type { ScheduleView } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';

interface Change {
  id: string;
  revision: number;
  label: string;
  opCount: number;
  by: string | null;
  at: string;
  undoneAt: string | null;
  undoneBy: string | null;
  rollback: boolean;
}
const when = (s: string) =>
  new Date(s).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function JournalPanel({ view, onView }: { view: ScheduleView; onView: (v: ScheduleView) => void }) {
  const qc = useQueryClient();
  const id = view.schedule.id;
  const q = useQuery({
    queryKey: ['schedule-journal', id, view.schedule.revision],
    queryFn: () => api<Change[]>(`/schedules/${id}/changes`),
  });
  const [ask, setAsk] = useState<Change | null>(null);
  const m = useMutation({
    mutationFn: (changeId: string) => api<ScheduleView>(`/schedules/${id}/rollback`, { body: { changeId } }),
    onSuccess: (v) => {
      onView(v);
      setAsk(null);
      qc.invalidateQueries({ queryKey: ['schedule-journal', id] });
      toast('График возвращён. Откат можно отменить — он в журнале', 'success');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const list = q.data;
  if (!list.length)
    return (
      <EmptyState title="Правок пока нет">
        Здесь появятся правки графика: кто, что и когда менял. Любую можно откатить.
      </EmptyState>
    );
  const active = list.filter((c) => !c.undoneAt);
  const after = ask ? active.filter((c) => c.at >= ask.at).length : 0;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-ink-2">
        Последние правки графика. Откат возвращает работы, связи и узлы ИСР к состоянию до выбранной правки;
        правки после неё откатываются тоже. Назначения ресурсов удалённых работ откат не возвращает. Подробная
        история по полям — кнопкой «История» в карточке работы.
      </p>
      <ul className="divide-y divide-line rounded-md border border-line bg-surface">
        {list.map((c) => (
          <li
            key={c.id}
            className={clsx(
              'flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2',
              c.undoneAt && 'opacity-60',
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[14px]">
                {c.label}
                {c.rollback ? (
                  <Badge tone="info" className="ml-2">
                    откат
                  </Badge>
                ) : null}
              </span>
              <span className="block text-[12px] text-ink-2">
                {when(c.at)}
                {c.by ? ` · ${c.by}` : ''} · правка № {c.revision}
                {c.undoneAt ? ` · откачена ${when(c.undoneAt)}${c.undoneBy ? `, ${c.undoneBy}` : ''}` : ''}
              </span>
            </span>
            {view.can.edit && view.schedule.status !== 'approved' && !c.undoneAt ? (
              <Button size="sm" onClick={() => setAsk(c)} className="max-md:h-11">
                Вернуть как было до неё
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {ask ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setAsk(null)}
          title="Откатить правки?"
          description={`График вернётся к состоянию до правки «${ask.label}». Откатится правок: ${after}.`}
          width="sm"
          footer={
            <>
              <Button onClick={() => setAsk(null)} className="max-md:h-11">
                Отмена
              </Button>
              <Button
                variant="primary"
                loading={m.isPending}
                onClick={() => m.mutate(ask.id)}
                className="max-md:h-11"
              >
                Откатить
              </Button>
            </>
          }
        >
          <p className="text-[14px]">
            Откат сам попадёт в журнал — если передумаете, его тоже можно откатить.
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}
